import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import * as XLSX from "xlsx";
import Navbar from "@/components/Navbar";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { FacilitatorPicker } from "@/components/FacilitatorPicker";
import { JourneyBuilder } from "@/components/JourneyBuilder";
import { toast } from "sonner";
import {
  Plus, ArrowLeft, Calendar, Building2, ArrowUpRight, Upload, FileSpreadsheet, Download, Copy,
} from "lucide-react";

const STATUS_BADGE = {
  upcoming: "bg-blue-50 text-blue-800 border-blue-200",
  active: "bg-emerald-50 text-emerald-800 border-emerald-200",
  completed: "bg-stone-100 text-stone-700 border-stone-200",
};

export default function AdminCompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [batches, setBatches] = useState([]);
  const [participants, setParticipants] = useState([]);
  const [loading, setLoading] = useState(true);

  // batch dialog
  const [bOpen, setBOpen] = useState(false);
  const [bForm, setBForm] = useState({
    name: "",
    program_name: "Shifting Gears – Core",
    facilitator_name: "",
    facilitator_user_id: null,
    start_date: "",
    end_date: "",
    max_roleplays: "",
    journey_slug: "",
  });
  const [savingBatch, setSavingBatch] = useState(false);
  const [facilitators, setFacilitators] = useState([]);
  const [journeys, setJourneys] = useState([]);
  const [journeyOptions, setJourneyOptions] = useState([]);
  const [builderOpen, setBuilderOpen] = useState(false);
  const [editingJourney, setEditingJourney] = useState(null);

  const loadJourneys = () => {
    api.get(`/admin/companies/${id}/journeys`).then((r) => setJourneys(r.data)).catch(() => setJourneys([]));
    api.get(`/admin/journey-options?company_id=${id}`).then((r) => setJourneyOptions(r.data.journeys || [])).catch(() => setJourneyOptions([]));
  };

  useEffect(() => {
    api.get("/admin/facilitators")
      .then((r) => setFacilitators(r.data))
      .catch(() => setFacilitators([]));
    loadJourneys();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // participant dialog
  const [pOpen, setPOpen] = useState(false);
  const [pForm, setPForm] = useState({ employee_id: "", name: "", email: "", department: "", designation: "" });
  const [savingP, setSavingP] = useState(false);

  // bulk upload
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const reload = () => {
    setLoading(true);
    Promise.all([
      api.get(`/admin/companies/${id}`),
      api.get(`/admin/companies/${id}/batches`),
      api.get(`/admin/companies/${id}/participants`),
    ])
      .then(([c, b, p]) => {
        setCompany(c.data);
        setBatches(b.data);
        setParticipants(p.data);
      })
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(reload, [id]);

  const createBatch = async (e) => {
    e.preventDefault();
    setSavingBatch(true);
    try {
      const payload = {
        ...bForm,
        max_roleplays: bForm.max_roleplays === "" ? null : parseInt(bForm.max_roleplays, 10),
      };
      if (!payload.journey_slug) delete payload.journey_slug;
      const r = await api.post(`/admin/companies/${id}/batches`, payload);
      toast.success("Batch created");
      setBOpen(false);
      setBForm({ name: "", program_name: "Shifting Gears – Core", facilitator_name: "", facilitator_user_id: null, start_date: "", end_date: "", max_roleplays: "", journey_slug: "" });
      navigate(`/admin/batches/${r.data.id}`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingBatch(false);
    }
  };

  const openCreateJourney = () => { setEditingJourney(null); setBuilderOpen(true); };
  const openEditJourney = (j) => { setEditingJourney(j); setBuilderOpen(true); };
  const deleteJourney = async (j) => {
    if (!window.confirm(`Delete journey "${j.name}"? Batches using it will fall back to the default journey.`)) return;
    try {
      await api.delete(`/admin/companies/${id}/journeys/${j.id}`);
      toast.success("Journey deleted");
      loadJourneys();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const downloadCredentialsXlsx = (rows, filename) => {
    const withTemp = (rows || []).filter((p) => p.temp_password);
    if (withTemp.length === 0) {
      toast.info("No credentials to download — all participants have already set their own password.");
      return;
    }
    const data = withTemp.map((p) => ({
      "Employee ID": p.employee_id || "",
      "Name": p.name || "",
      "Email": p.email || "",
      "Temporary Password": p.temp_password || "",
    }));
    const ws = XLSX.utils.json_to_sheet(data);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Credentials");
    XLSX.writeFile(wb, filename);
    toast.success(`Downloaded ${withTemp.length} credential(s)`);
  };

  const companyFileSlug = () => (company?.name || "company").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

  const createParticipant = async (e) => {
    e.preventDefault();
    setSavingP(true);    try {
      const r = await api.post(`/admin/companies/${id}/participants`, pForm);
      toast.success(`Participant created · temporary password: ${r.data.temp_password}`, { duration: 9000 });
      setPOpen(false);
      setPForm({ employee_id: "", name: "", email: "", department: "", designation: "" });
      reload();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSavingP(false);
    }
  };

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    setBulkResult(null);
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: "array" });
      const sheet = wb.Sheets[wb.SheetNames[0]];
      const rows = XLSX.utils.sheet_to_json(sheet, { defval: "" });
      const participants = rows.map((row) => ({
        name: String(row.name || row.Name || "").trim(),
        email: String(row.email || row.Email || "").trim().toLowerCase(),
      })).filter((p) => p.email && p.name);
      if (participants.length === 0) {
        toast.error("No valid rows found. Required columns: Name, Email");
        return;
      }
      const r = await api.post(`/admin/companies/${id}/participants/bulk`, { participants });
      setBulkResult(r.data);
      toast.success(`${r.data.created.length} added, ${r.data.skipped.length} skipped`);
      reload();
    } catch (err) {
      toast.error(formatApiError(err) || "Failed to parse the spreadsheet");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const sortedParticipants = useMemo(
    () => [...participants].sort((a, b) => a.name.localeCompare(b.name)),
    [participants],
  );

  const downloadTemplate = () => {
    const headers = ["Name", "Email"];
    const example = [
      ["Rahul Singh", "rahul.singh@example.com"],
      ["Sneha Verma", "sneha.verma@example.com"],
    ];
    const ws = XLSX.utils.aoa_to_sheet([headers, ...example]);
    ws["!cols"] = [{ wch: 22 }, { wch: 28 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Participants");
    XLSX.writeFile(wb, "leadersim-participants-template.xlsx");
    toast.success("Template downloaded");
  };

  const copyCreds = async (p) => {
    if (!p.temp_password) {
      toast.info(`${p.name} has already set their own password.`);
      return;
    }
    const line = `${p.name} · ${p.email} · Temporary password: ${p.temp_password}`;
    try {
      await navigator.clipboard.writeText(line);
      toast.success("Copied — paste it in WhatsApp or email");
    } catch {
      toast.error("Clipboard blocked by the browser");
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50">
        <Navbar />
        <div className="max-w-7xl mx-auto px-6 py-12 text-stone-500 text-sm">Loading…</div>
      </div>
    );
  }
  if (!company) return null;

  return (
    <div className="min-h-screen bg-stone-50">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 sm:px-10 py-10">
        <Link to="/admin/companies" className="inline-flex items-center text-sm text-stone-500 hover:text-stone-800 mb-5">
          <ArrowLeft className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> All companies
        </Link>

        <div className="flex items-center gap-5 mb-8">
          {company.logo_url ? (
            <img src={company.logo_url} alt="" className="w-16 h-16 rounded-2xl object-cover bg-stone-100 border border-stone-200" />
          ) : (
            <div className="w-16 h-16 rounded-2xl bg-stone-100 border border-stone-200 grid place-items-center">
              <Building2 className="w-7 h-7 text-stone-500" strokeWidth={1.5} />
            </div>
          )}
          <div>
            <h1 className="font-display text-3xl md:text-4xl text-stone-900 tracking-tight">{company.name}</h1>
            <div className="text-sm text-stone-500 mt-1">
              {company.batches_count} batches · {company.participants_count} participants
            </div>
          </div>
        </div>

        <Tabs defaultValue="batches">
          <TabsList className="bg-stone-100">
            <TabsTrigger value="batches" data-testid="tab-batches">Batches</TabsTrigger>
            <TabsTrigger value="participants" data-testid="tab-participants">Participants</TabsTrigger>
            <TabsTrigger value="journeys" data-testid="tab-journeys">Learning Journeys</TabsTrigger>
          </TabsList>

          <TabsContent value="batches" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="new-batch-btn" onClick={() => setBOpen(true)} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white rounded-md h-10 px-5">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> New batch
              </Button>
            </div>
            {batches.length === 0 ? (
              <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-12 text-center text-stone-500">
                No batches yet. Create one to start enrolling participants.
              </div>
            ) : (
              <div className="space-y-3" data-testid="batches-list">
                {batches.map((b) => (
                  <Link key={b.id} to={`/admin/batches/${b.id}`} data-testid={`batch-${b.id}`} className="flex items-center gap-4 bg-white border border-stone-200 rounded-2xl p-5 hover:border-[#b81f24]/30 transition-all">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <span className={`text-[10px] tracking-[0.18em] uppercase px-2 py-0.5 rounded-full border ${STATUS_BADGE[b.status] || STATUS_BADGE.upcoming}`}>{b.status}</span>
                        <span className="text-[10px] tracking-[0.18em] uppercase text-stone-500">{b.program_name}</span>
                      </div>
                      <div className="font-display text-lg text-stone-900 truncate">{b.name}</div>
                      <div className="text-xs text-stone-500 flex items-center gap-3 mt-1 flex-wrap">
                        <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" strokeWidth={1.5} />{b.start_date} → {b.end_date}</span>
                        <span>Facilitator: {b.facilitator_name}</span>
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl text-stone-900">{b.participants_count}</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">participants</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl text-stone-900">{b.completion_pct}%</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">complete</div>
                    </div>
                    <div className="text-right">
                      <div className="font-display text-xl text-stone-900">{b.average_score ?? "—"}</div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-stone-500">avg score</div>
                    </div>
                    <ArrowUpRight className="w-4 h-4 text-stone-400" strokeWidth={1.75} />
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="participants" className="mt-6">
            <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
              <div className="text-sm text-stone-600">{participants.length} participants in this company</div>
              <div className="flex gap-2">
                <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" className="hidden" onChange={handleFile} data-testid="xlsx-upload-input" />
                <Button
                  data-testid="upload-xlsx-btn"
                  variant="outline"
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading}
                  className="rounded-md h-10 border-stone-300"
                >
                  <Upload className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                  {uploading ? "Uploading…" : "Upload Excel"}
                </Button>
                <Button
                  data-testid="download-credentials-btn"
                  variant="outline"
                  onClick={() => downloadCredentialsXlsx(participants, `${companyFileSlug()}-credentials.xlsx`)}
                  className="rounded-md h-10 border-stone-300"
                >
                  <Download className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                  Download Credentials (Excel)
                </Button>
                <Button data-testid="new-participant-btn" onClick={() => setPOpen(true)} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white rounded-md h-10 px-5">
                  <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Add participant
                </Button>
              </div>
            </div>

            {bulkResult && (
              <div className="bg-emerald-50 border border-emerald-200 text-emerald-900 rounded-2xl p-4 mb-4 text-sm">
                <div className="font-medium mb-1">Bulk upload complete</div>
                <div>Added: <b>{bulkResult.created.length}</b> · Skipped: <b>{bulkResult.skipped.length}</b></div>
                <div className="mt-1 text-xs">Each participant received a unique temporary password. Use <b>Copy creds</b> on each row to share it — they&apos;ll be asked to change it on first login.</div>
                <Button
                  data-testid="download-new-credentials-btn"
                  variant="outline"
                  onClick={() => downloadCredentialsXlsx(bulkResult.created, `${companyFileSlug()}-new-credentials.xlsx`)}
                  className="rounded-md h-9 mt-2 border-emerald-300 text-emerald-800 hover:bg-emerald-100 bg-white"
                >
                  <Download className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                  Download New Credentials
                </Button>
                {bulkResult.skipped.length > 0 && (
                  <details className="mt-2 text-xs">
                    <summary className="cursor-pointer">View skipped</summary>
                    <ul className="mt-1 space-y-0.5">{bulkResult.skipped.map((s, i) => <li key={i}>{s.email} — {s.reason}</li>)}</ul>
                  </details>
                )}
              </div>
            )}

            {participants.length === 0 ? (
              <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-12 text-center text-stone-500">
                <FileSpreadsheet className="w-10 h-10 text-stone-300 mx-auto mb-3" strokeWidth={1.25} />
                <div>No participants yet. Add manually or upload an Excel file with columns: <b>Name, Email</b>.</div>
              </div>
            ) : (
              <div className="bg-white border border-stone-200 rounded-2xl overflow-hidden" data-testid="participants-table">
                <table className="w-full text-sm">
                  <thead className="bg-stone-50 text-stone-500 text-[10px] tracking-[0.18em] uppercase">
                    <tr>
                      <th className="text-left px-5 py-3">Employee ID</th>
                      <th className="text-left px-5 py-3">Name</th>
                      <th className="text-left px-5 py-3">Email</th>
                      <th className="text-left px-5 py-3">Department</th>
                      <th className="text-left px-5 py-3">Temp password</th>
                      <th className="px-5 py-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedParticipants.map((p) => (
                      <tr key={p.id} className="border-t border-stone-100 hover:bg-stone-50">
                        <td className="px-5 py-3 text-stone-700 font-mono text-xs">{p.employee_id || "—"}</td>
                        <td className="px-5 py-3 text-stone-900 font-medium">
                          <span className="inline-flex items-center gap-2">
                            {p.name}
                            {p.must_change_password && (
                              <span data-testid={`pending-badge-${p.id}`} className="text-[9px] tracking-[0.12em] uppercase px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-700 border border-amber-200">Pending</span>
                            )}
                          </span>
                        </td>
                        <td className="px-5 py-3 text-stone-600">{p.email}</td>
                        <td className="px-5 py-3 text-stone-600">{p.department || "—"}</td>
                        <td className="px-5 py-3">
                          {p.temp_password ? (
                            <code data-testid={`temp-password-${p.id}`} className="text-xs bg-stone-100 text-stone-800 px-1.5 py-0.5 rounded">{p.temp_password}</code>
                          ) : (
                            <span className="text-xs text-stone-400">set by user</span>
                          )}
                        </td>
                        <td className="px-5 py-3 text-right">
                          <button
                            type="button"
                            data-testid={`copy-creds-${p.id}-btn`}
                            onClick={() => copyCreds(p)}
                            title="Copy: name · email · default password"
                            className="text-stone-400 hover:text-[#b81f24] inline-flex items-center gap-1 text-xs"
                          >
                            <Copy className="w-3.5 h-3.5" strokeWidth={1.75} />
                            Copy creds
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </TabsContent>

          <TabsContent value="journeys" className="mt-6">
            <div className="flex justify-end mb-4">
              <Button data-testid="new-journey-btn" onClick={openCreateJourney} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white rounded-md h-10 px-5">
                <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75} /> Create journey
              </Button>
            </div>
            {journeys.length === 0 ? (
              <div className="bg-white border border-dashed border-stone-200 rounded-3xl p-12 text-center text-stone-500">
                No custom journeys yet. Create one to assign a tailored set of scenarios to this company&apos;s batches.
              </div>
            ) : (
              <div className="space-y-3" data-testid="journeys-list">
                {journeys.map((j) => (
                  <div key={j.id} data-testid={`journey-${j.id}`} className="flex items-center gap-4 bg-white border border-stone-200 rounded-2xl p-5">
                    <div className="flex-1 min-w-0">
                      <div className="font-display text-lg text-stone-900 truncate">{j.name}</div>
                      {j.description && <div className="text-sm text-stone-500 truncate">{j.description}</div>}
                      <div className="text-xs text-stone-500 flex items-center gap-3 mt-1 flex-wrap">
                        <span>{(j.weeks || []).length} week{(j.weeks || []).length === 1 ? "" : "s"}</span>
                        <span>·</span>
                        <span>{(j.scenario_ids || []).length} scenarios</span>
                        <span>·</span>
                        <span>{(j.categories || []).length} categories</span>
                      </div>
                    </div>
                    <Button data-testid={`edit-journey-${j.id}-btn`} variant="outline" onClick={() => openEditJourney(j)} className="rounded-md h-9 border-stone-300">Edit</Button>
                    <Button data-testid={`delete-journey-${j.id}-btn`} variant="outline" onClick={() => deleteJourney(j)} className="rounded-md h-9 border-red-200 text-red-700 hover:bg-red-50">Delete</Button>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

        </Tabs>
      </div>

      <JourneyBuilder
        open={builderOpen}
        onOpenChange={setBuilderOpen}
        companyId={id}
        journey={editingJourney}
        onSaved={loadJourneys}
      />

      {/* New batch dialog */}
      <Dialog open={bOpen} onOpenChange={setBOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">New batch</DialogTitle></DialogHeader>
          <form onSubmit={createBatch} className="space-y-4 mt-2">
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Batch name *</Label>
              <Input data-testid="batch-name-input" required value={bForm.name} onChange={(e) => setBForm({ ...bForm, name: e.target.value })} placeholder="e.g. FTM Cohort 1 – Q1 2026" />
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Program name *</Label>
              <Input data-testid="batch-program-input" required value={bForm.program_name} onChange={(e) => setBForm({ ...bForm, program_name: e.target.value })} />
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Facilitator *</Label>
              <FacilitatorPicker
                testid="batch-facilitator-select"
                facilitators={facilitators}
                name={bForm.facilitator_name}
                userId={bForm.facilitator_user_id}
                onChange={({ facilitator_name, facilitator_user_id }) => setBForm({ ...bForm, facilitator_name, facilitator_user_id })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600 mb-1.5 block">Start date *</Label>
                <Input data-testid="batch-start-input" type="date" required value={bForm.start_date} onChange={(e) => setBForm({ ...bForm, start_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-stone-600 mb-1.5 block">End date *</Label>
                <Input data-testid="batch-end-input" type="date" required value={bForm.end_date} onChange={(e) => setBForm({ ...bForm, end_date: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Max roleplays per participant</Label>
              <Input
                data-testid="batch-max-roleplays-input"
                type="number"
                min="1"
                value={bForm.max_roleplays}
                onChange={(e) => setBForm({ ...bForm, max_roleplays: e.target.value })}
                placeholder="Leave empty for unlimited"
              />
              <p className="text-[11px] text-stone-400 mt-1">Optional — caps how many practice sessions each participant can run. Empty = unlimited.</p>
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Learning journey</Label>
              <select
                data-testid="batch-journey-select"
                value={bForm.journey_slug}
                onChange={(e) => setBForm({ ...bForm, journey_slug: e.target.value })}
                className="w-full h-10 rounded-md border border-stone-200 bg-white px-3 text-sm text-stone-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#b81f24]/20"
              >
                <option value="">Default journey</option>
                {journeyOptions.map((j) => (
                  <option key={j.slug} value={j.slug}>{j.custom ? "★ " : ""}{j.name}{j.custom ? " (custom)" : ""}</option>
                ))}
              </select>
              <p className="text-[11px] text-stone-400 mt-1">Custom journeys for this company appear at the top. Build them in the Journeys tab.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setBOpen(false)}>Cancel</Button>
              <Button data-testid="save-batch-btn" type="submit" disabled={savingBatch} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white">{savingBatch ? "Saving…" : "Create batch"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* New participant dialog */}
      <Dialog open={pOpen} onOpenChange={setPOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-display">Add participant</DialogTitle></DialogHeader>
          <form onSubmit={createParticipant} className="space-y-4 mt-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600 mb-1.5 block">Employee ID *</Label>
                <Input data-testid="p-employee-id-input" required value={pForm.employee_id} onChange={(e) => setPForm({ ...pForm, employee_id: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-stone-600 mb-1.5 block">Full name *</Label>
                <Input data-testid="p-name-input" required value={pForm.name} onChange={(e) => setPForm({ ...pForm, name: e.target.value })} />
              </div>
            </div>
            <div>
              <Label className="text-xs text-stone-600 mb-1.5 block">Email *</Label>
              <Input data-testid="p-email-input" required type="email" value={pForm.email} onChange={(e) => setPForm({ ...pForm, email: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs text-stone-600 mb-1.5 block">Department</Label>
                <Input data-testid="p-dept-input" value={pForm.department} onChange={(e) => setPForm({ ...pForm, department: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs text-stone-600 mb-1.5 block">Designation</Label>
                <Input data-testid="p-desig-input" value={pForm.designation} onChange={(e) => setPForm({ ...pForm, designation: e.target.value })} />
              </div>
            </div>
            <div className="bg-stone-50 border border-stone-200 rounded-xl p-3 text-xs text-stone-600">
              Default password will be <code className="bg-white px-1.5 py-0.5 rounded">Welcome@123</code> — participants can sign in with their email + this password.
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setPOpen(false)}>Cancel</Button>
              <Button data-testid="save-participant-btn" type="submit" disabled={savingP} className="bg-[#b81f24] hover:bg-[#9a1a1f] text-white">{savingP ? "Saving…" : "Create participant"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
